/**
 * expenseService — direct Axios calls for Finance (expense) mutations.
 *
 * Pattern: GET endpoints live in appApi.js (RTK Query).
 *          POST / PATCH live here as direct service calls.
 *
 * Authorization header is set globally by authSlice after login, so
 * these functions need no manual header injection.
 */

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

/**
 * Fetch the paginated expense daybook.
 *
 * Kept as a direct Axios call (not RTK Query) so components that need a
 * fresh pull after a mutation can call it imperatively without touching
 * global cache state.
 *
 * @param {object} params - { page, categoryId, dateFrom, dateTo, search }
 * @returns {Promise<{ finance: array, total: number }>}
 */
export const getAllExpenses = (params = {}) => {
  return axios.get(`${API_BASE_URL}/api/finance/daybook`, { params });
};

/**
 * Create a new finance entry.
 *
 * Payload shape:
 * {
 *   type:       "income" | "expense",
 *   amount:     number,
 *   categoryId: string (ObjectId),
 *   paidBy:     string (ObjectId – user who paid),
 *   desc:       string,
 *   isVehicle:  boolean,
 *   vehicleId:  string (ObjectId) – required when isVehicle is true
 * }
 *
 * When isVehicle === true the backend sets isVehicleExpense and stores
 * the vehicleId on the Finance document automatically.
 *
 * @param {object} formData
 */
export const createExpense = (formData) => {
  return axios.post(`${API_BASE_URL}/api/finance/manage`, formData);
};

/**
 * Update an existing finance entry (PATCH).
 *
 * The backend enforces a 24-hour edit window for non-super-admin roles.
 * This call will receive a 403 response with a descriptive message if
 * the window has expired and the caller is not a super-admin.
 *
 * Updatable fields: amount, categoryId, paidBy, desc, isVehicle, vehicleId
 *
 * @param {string} id          - Finance document _id
 * @param {object} updatedData - Partial payload with changed fields only
 */
export const updateExpense = (id, updatedData) => {
  return axios.patch(`${API_BASE_URL}/api/finance/update/${id}`, updatedData);
};

/**
 * Create a new petty-cash expense category head.
 *
 * Payload shape: { categoryName: string, description?: string }
 *
 * POST — executed as a direct Axios call (not RTK Query) per architecture rules.
 *
 * @param {{ categoryName: string, description?: string }} data
 */
export const createFinanceCategory = (data) => {
  return axios.post(`${API_BASE_URL}/api/finance/create-category`, data);
};
